import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Check, Loader2, Download, Phone, Mail, Globe } from "lucide-react";
import { Reveal, SectionHeader } from "./Reveal";
import { API } from "../lib/api";

const RIDER = [
  "4 micros Shure SM58 + 4 pies jirafa",
  "2 pies soporte para bajo",
  "3 cajas de inyección (DI)",
  "1 Shure SM57 · 1 AKG 414",
  "5 monitores de escenario",
];

const WhatsAppIcon = ({ size = 14, className = "" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413" />
  </svg>
);

const EVENT_TYPES = ["Teatro", "Festival", "Institución cultural", "Evento privado", "Otro"];

const INITIAL = { name: "", email: "", organization: "", event_type: EVENT_TYPES[0], event_date: "", city: "", message: "" };

const inputCls =
  "w-full bg-coal border border-cream/15 px-4 py-3 text-cream placeholder:text-mute focus:border-gold/70 focus:outline-none transition-colors";
const labelCls = "block text-[11px] uppercase tracking-[0.25em] text-sand mb-2";

export const Booking = () => {
  const [form, setForm] = useState(INITIAL);
  const [sending, setSending] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Completa tu nombre, email y mensaje.");
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/bookings`, form);
      toast.success("Solicitud recibida. Te contactaremos muy pronto.");
      setForm(INITIAL);
    } catch {
      toast.error("No se pudo enviar. Inténtalo de nuevo o escríbenos por email.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contratacion" className="relative py-24 sm:py-32">
      <div className="mx-auto max-w-[1600px] px-6 sm:px-12 lg:px-24">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-14 lg:gap-20">
          <div>
            <SectionHeader
              eyebrow="Contratación"
              title={
                <>
                  Llevamos el bolero a <span className="italic text-gold">tu escenario</span>.
                </>
              }
            />
            <Reveal delay={0.1}>
              <p className="mt-8 text-base md:text-lg leading-relaxed text-sand">
                Teatros, festivales, instituciones culturales y eventos privados. El grupo adapta el repertorio a cada ocasión con setlists personalizados, en espacios abiertos o cerrados e indistintamente en formato íntimo o de gran escenario.
              </p>
            </Reveal>

            <Reveal delay={0.15}>
              <div className="mt-10 border border-gold/20 bg-card p-8" data-testid="agency-card">
                <p className="eyebrow">Agencia de management</p>
                <p className="mt-4 font-serif text-2xl md:text-3xl text-cream">Producciones SiBemol</p>
                <div className="mt-6 flex flex-wrap items-center gap-x-7 gap-y-5">
                  <a
                    data-testid="agency-logo-link"
                    href="https://www.produccionessibemol.es/"
                    target="_blank"
                    rel="noopener noreferrer"
                    title="www.produccionessibemol.es"
                    aria-label="Producciones SiBemol — página web oficial"
                    className="shrink-0 block transition-opacity duration-300 hover:opacity-75"
                  >
                    <img src="/logo/psb-logo.png" alt="Logo de Producciones SiBemol" className="h-16 w-auto md:h-20" />
                  </a>
                  <div className="min-w-0 space-y-4">
                    <div className="flex flex-wrap items-center gap-x-6 gap-y-3">
                      <a data-testid="agency-phone-link" href="tel:+34618551512" className="flex items-center gap-4 font-serif text-3xl md:text-4xl font-bold leading-tight text-cream transition-colors hover:text-gold">
                        <Phone size={26} className="shrink-0 text-gold" />
                        618 551 512
                      </a>
                      <a
                        data-testid="agency-whatsapp-link"
                        href="https://wa.me/34618551512?text=Hola%2C%20me%20gustar%C3%ADa%20contratar%20a%20Cuarto%20Bolero"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2.5 rounded-full border border-gold/50 px-5 py-2 text-[11px] uppercase tracking-[0.18em] text-gold transition-all duration-300 hover:bg-gold hover:text-ink"
                      >
                        <WhatsAppIcon size={14} /> WhatsApp
                      </a>
                    </div>
                    <a data-testid="agency-email-link" href="mailto:eventos@produccionessibemol.es" className="flex items-start gap-3 text-base text-sand hover:text-gold transition-colors break-all">
                      <Mail size={16} className="shrink-0 mt-1 text-gold" />
                      eventos@produccionessibemol.es
                    </a>
                    <a data-testid="agency-website-link" href="https://www.produccionessibemol.es/" target="_blank" rel="noopener noreferrer" className="flex items-start gap-3 text-base text-sand hover:text-gold transition-colors">
                      <Globe size={16} className="shrink-0 mt-1 text-gold" />
                      www.produccionessibemol.es
                    </a>
                  </div>
                </div>
              </div>
            </Reveal>

            <Reveal delay={0.2}>
              <div className="mt-8 border border-gold/20 bg-card p-8" data-testid="booking-rider-card">
                <p className="eyebrow">Rider técnico</p>
                <ul className="mt-5 space-y-3">
                  {RIDER.map((r) => (
                    <li key={r} className="flex items-start gap-3 text-sm text-sand">
                      <Check size={15} className="mt-0.5 shrink-0 text-gold" />
                      {r}
                    </li>
                  ))}
                </ul>
                <a
                  data-testid="rider-download-button"
                  href="/press-kit/DOSSIER_CUARTO_BOLERO.pdf"
                  download="CUARTO_BOLERO_Dossier.pdf"
                  className="mt-7 inline-flex items-center gap-3 rounded-full border border-gold/50 px-6 py-2.5 text-[11px] uppercase tracking-[0.18em] text-gold transition-all duration-300 hover:bg-gold hover:text-ink"
                >
                  <Download size={14} /> Dossier + rider (PDF)
                </a>
              </div>
            </Reveal>
          </div>

          <Reveal delay={0.1}>
            <form
              onSubmit={submit}
              data-testid="booking-form"
              className="border border-gold/15 bg-card p-8 md:p-10 space-y-6"
            >
              <p className="font-serif text-2xl text-cream">Solicita disponibilidad</p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div>
                  <label className={labelCls} htmlFor="booking-name">Nombre *</label>
                  <input id="booking-name" data-testid="booking-name-input" className={inputCls} placeholder="Tu nombre" value={form.name} onChange={set("name")} />
                </div>
                <div>
                  <label className={labelCls} htmlFor="booking-email">Email *</label>
                  <input id="booking-email" data-testid="booking-email-input" type="email" className={inputCls} placeholder="tu@email.com" value={form.email} onChange={set("email")} />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div>
                  <label className={labelCls} htmlFor="booking-org">Organización</label>
                  <input id="booking-org" data-testid="booking-org-input" className={inputCls} placeholder="Teatro, festival, empresa…" value={form.organization} onChange={set("organization")} />
                </div>
                <div>
                  <label className={labelCls} htmlFor="booking-type">Tipo de evento</label>
                  <div className="relative">
                    <select id="booking-type" data-testid="booking-type-select" className={`${inputCls} appearance-none pr-10`} value={form.event_type} onChange={set("event_type")}>
                      {EVENT_TYPES.map((t) => (
                        <option key={t} value={t}>
                          {t}
                        </option>
                      ))}
                    </select>
                    <span className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-gold text-xs">▼</span>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div>
                  <label className={labelCls} htmlFor="booking-date">Fecha prevista</label>
                  <input id="booking-date" data-testid="booking-date-input" type="date" className={inputCls} value={form.event_date} onChange={set("event_date")} />
                </div>
                <div>
                  <label className={labelCls} htmlFor="booking-city">Ciudad</label>
                  <input id="booking-city" data-testid="booking-city-input" className={inputCls} placeholder="Ciudad" value={form.city} onChange={set("city")} />
                </div>
              </div>

              <div>
                <label className={labelCls} htmlFor="booking-message">Mensaje *</label>
                <textarea id="booking-message" data-testid="booking-message-input" rows={4} className={`${inputCls} resize-none`} placeholder="Cuéntanos cómo sería tu evento…" value={form.message} onChange={set("message")} />
              </div>

              <button
                type="submit"
                data-testid="booking-submit-button"
                disabled={sending}
                className="w-full rounded-full bg-gold px-8 py-4 text-[13px] font-medium uppercase tracking-[0.2em] text-ink transition-all duration-300 hover:shadow-[0_10px_40px_rgba(212,175,55,0.35)] disabled:opacity-60 disabled:cursor-not-allowed inline-flex items-center justify-center gap-3"
              >
                {sending && <Loader2 size={16} className="animate-spin" />}
                {sending ? "Enviando…" : "Enviar solicitud"}
              </button>
            </form>
          </Reveal>
        </div>
      </div>
    </section>
  );
};
